import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows, Transitions } from '../../styles/ThemeStyles';
import { LucideIcon, ChevronDown, ChevronRight } from 'lucide-react';

interface SubTab {
  id: string;
  label: string;
  content: React.ReactNode;
  icon?: LucideIcon;
}

interface Tab {
  id: string;
  label: string;
  content: React.ReactNode;
  icon?: LucideIcon;
  subTabs?: SubTab[];
}

interface TabbedInterfaceProps {
  tabs: Tab[];
  defaultActiveTab?: string;
  defaultActiveSubTab?: string;
  activeTab?: string; // External control of active tab
  variant?: 'desktop' | 'mobile';
  middleSection?: React.ReactNode;
  showMiddleSectionForTabs?: string[];
  onTabChange?: (tabId: string) => void; // Callback when tab changes
}

const TabContainer = styled.div<{ variant: 'desktop' | 'mobile' }>`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  background: ${ThemeColors.background.cards};
  border-radius: ${BorderRadius.xl};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(76, 175, 80, 0.08);
  
  @media (max-width: 768px) {
    max-width: 100%;
    margin: 0;
    border-radius: ${BorderRadius.lg};
  }
`;

const TabHeader = styled.div<{ variant: 'desktop' | 'mobile' }>`
  display: flex;
  background: linear-gradient(135deg, #ffffff 0%, #f8fffe 100%);
  border-radius: ${BorderRadius.xl} ${BorderRadius.xl} 0 0;
  padding: ${props => props.variant === 'desktop' ? `${Spacing.md} ${Spacing.lg}` : `${Spacing.sm} ${Spacing.md}`};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border-bottom: 2px solid rgba(76, 175, 80, 0.1);
  overflow-x: auto;
  position: sticky;
  top: 0;
  z-index: 100;
  margin-bottom: 0;
  justify-content: ${props => props.variant === 'desktop' ? 'center' : 'flex-start'};
  align-items: center;
  gap: ${props => props.variant === 'desktop' ? Spacing.sm : Spacing.xs};
  
  ${props => props.variant === 'mobile' && `
    flex-direction: column;
    border-bottom: none;
    padding: ${Spacing.md};
    gap: ${Spacing.xs};
  `}
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.03);
    border-radius: ${BorderRadius.sm};
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(90deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark});
    border-radius: ${BorderRadius.sm};
    transition: background 0.3s ease;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(90deg, ${ThemeColors.primary.dark}, ${ThemeColors.primary.main});
  }
`;

const TabButton = styled.button<{ 
  isActive: boolean; 
  variant: 'desktop' | 'mobile';
  hasSubTabs?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${props => props.variant === 'desktop' ? `${Spacing.md} ${Spacing.lg}` : `${Spacing.sm} ${Spacing.md}`};
  border: none;
  background: ${props => props.isActive 
    ? 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)' 
    : 'linear-gradient(135deg, #ffffff 0%, #f8fffe 100%)'};
  color: ${props => props.isActive ? '#ffffff' : ThemeColors.text.primary};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: ${props => props.isActive ? Typography.fontWeight.bold : Typography.fontWeight.semiBold};
  font-size: ${props => props.variant === 'desktop' ? Typography.fontSize.base : Typography.fontSize.sm};
  white-space: nowrap;
  position: relative;
  border-radius: ${BorderRadius.lg};
  min-width: ${props => props.variant === 'desktop' ? '160px' : '100%'};
  justify-content: center;
  box-shadow: ${props => props.isActive 
    ? '0 4px 16px rgba(76, 175, 80, 0.25), 0 2px 8px rgba(0, 0, 0, 0.08)' 
    : '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.06)'};
  border: ${props => props.isActive 
    ? '1px solid rgba(255, 255, 255, 0.2)' 
    : '1px solid rgba(76, 175, 80, 0.08)'};
  
  &:hover {
    background: ${props => props.isActive 
      ? 'linear-gradient(135deg, #388E3C 0%, #2E7D32 100%)' 
      : 'linear-gradient(135deg, #f8fffe 0%, #e8f5e8 100%)'};
    color: ${props => props.isActive ? '#ffffff' : ThemeColors.primary.main};
    transform: translateY(-1px);
    box-shadow: ${props => props.isActive 
      ? '0 6px 20px rgba(76, 175, 80, 0.3), 0 3px 12px rgba(0, 0, 0, 0.1)' 
      : '0 4px 16px rgba(76, 175, 80, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)'};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.2);
  }
  
  &:active {
    transform: translateY(0);
    transition: transform 0.1s ease;
  }
  
  ${props => props.isActive && `
    &::before {
      content: '';
      position: absolute;
      top: -1px;
      left: -1px;
      right: -1px;
      bottom: -1px;
      background: linear-gradient(135deg, #4CAF50, #388E3C, #2E7D32);
      border-radius: ${BorderRadius.lg};
      z-index: -1;
      opacity: 0.2;
    }
  `}
  
  ${props => props.variant === 'mobile' && `
    width: 100%;
    min-width: unset;
    margin-bottom: ${Spacing.sm};
    
    &:last-child {
      margin-bottom: 0;
    }
  `}
`;

const TabIcon = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.isActive ? '#ffffff' : ThemeColors.primary.main};
  transition: all 0.3s ease;
  filter: ${props => props.isActive ? 'drop-shadow(0 1px 3px rgba(0, 0, 0, 0.2))' : 'none'};
`;

const TabLabel = styled.span<{ isCollapsed: boolean }>`
  display: ${props => props.isCollapsed ? 'none' : 'block'};
  font-family: ${Typography.fontFamily.secondary};
  font-size: inherit;
  font-weight: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.025em;
  text-shadow: ${props => props.isCollapsed ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.05)'};
`;

const SubTabContainer = styled.div<{ isExpanded: boolean }>`
  background: linear-gradient(135deg, #f8fffe 0%, #e8f5e8 100%);
  border-bottom: 1px solid rgba(76, 175, 80, 0.08);
  max-height: ${props => props.isExpanded ? '200px' : '0'};
  overflow: hidden;
  transition: max-height ${Transitions.duration.standard} ${Transitions.easing.easeInOut};
  position: sticky;
  top: ${props => props.isExpanded ? '80px' : '0'};
  z-index: 99;
  border-radius: 0 0 ${BorderRadius.xl} ${BorderRadius.xl};
  box-shadow: ${props => props.isExpanded ? '0 2px 8px rgba(0, 0, 0, 0.04)' : 'none'};
`;

const MiddleSection = styled.div`
  background: white;
  padding: ${Spacing.lg} 0;
  border-bottom: 1px solid rgba(76, 175, 80, 0.08);
  position: sticky;
  top: 60px;
  z-index: 98;
`;

const SubTabHeader = styled.div<{ variant?: 'desktop' | 'mobile' }>`
  display: flex;
  flex-direction: ${props => props.variant === 'mobile' ? 'column' : 'row'};
  padding: ${Spacing.sm} ${Spacing.lg};
  gap: ${Spacing.sm};
  overflow-x: ${props => props.variant === 'mobile' ? 'visible' : 'auto'};
  overflow-y: ${props => props.variant === 'mobile' ? 'visible' : 'hidden'};
  
  @media (max-width: 768px) {
    flex-direction: column;
    overflow-x: visible;
    overflow-y: visible;
  }
  
  &::-webkit-scrollbar {
    height: 3px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: ${ThemeColors.gray[300]};
    border-radius: ${BorderRadius.sm};
  }
`;

const SubTabButton = styled.button<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  background: ${props => props.isActive 
    ? 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)' 
    : 'transparent'};
  border: ${props => props.isActive 
    ? '1px solid rgba(255, 255, 255, 0.2)' 
    : '1px solid transparent'};
  color: ${props => props.isActive ? '#ffffff' : ThemeColors.text.secondary};
  cursor: pointer;
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};
  font-weight: ${props => props.isActive ? Typography.fontWeight.semiBold : Typography.fontWeight.medium};
  font-size: ${Typography.fontSize.sm};
  white-space: nowrap;
  border-radius: ${BorderRadius.md};
  box-shadow: ${props => props.isActive 
    ? '0 2px 8px rgba(76, 175, 80, 0.25)' 
    : 'none'};
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
    margin-bottom: ${Spacing.xs};
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  &:hover {
    background: ${props => props.isActive 
      ? 'linear-gradient(135deg, #388E3C 0%, #2E7D32 100%)' 
      : 'rgba(76, 175, 80, 0.08)'};
    color: ${props => props.isActive ? '#ffffff' : ThemeColors.primary.main};
    transform: translateY(-1px);
    box-shadow: ${props => props.isActive 
      ? '0 4px 12px rgba(76, 175, 80, 0.3)' 
      : '0 2px 6px rgba(76, 175, 80, 0.12)'};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }
`;

const SubTabIcon = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.isActive ? '#ffffff' : ThemeColors.text.secondary};
  transition: all 0.3s ease;
  filter: ${props => props.isActive ? 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))' : 'none'};
`;

const SubTabLabel = styled.span`
  font-family: ${Typography.fontFamily.secondary};
  font-size: ${Typography.fontSize.sm};
  font-weight: inherit;
  letter-spacing: 0.025em;
`;

const TabContent = styled.div<{ variant: 'desktop' | 'mobile' }>`
  padding: ${props => props.variant === 'mobile' ? Spacing.md : Spacing.lg};
  min-height: ${props => props.variant === 'mobile' ? '400px' : '500px'};
  background: linear-gradient(135deg, #ffffff 0%, #f8fffe 100%);
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  border-radius: 0 0 ${BorderRadius.xl} ${BorderRadius.xl};
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(76, 175, 80, 0.05);
  
  @media (max-width: 768px) {
    max-width: 100%;
    margin: 0;
    border-radius: 0 0 ${BorderRadius.lg} ${BorderRadius.lg};
    padding: ${Spacing.md};
  }
`;

// Enhanced Product Selection Section
const ProductSelectionSection = styled.div`
  background: white;
  border-radius: ${BorderRadius.lg};
  padding: ${Spacing.xl};
  margin-bottom: ${Spacing.xl};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06), 0 1px 4px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(76, 175, 80, 0.08);
  border-left: 4px solid ${ThemeColors.primary.main};
  
  .section-header {
    display: flex;
    align-items: center;
    gap: ${Spacing.md};
    margin-bottom: ${Spacing.lg};
    
    .icon {
      background: linear-gradient(135deg, ${ThemeColors.primary.light}, ${ThemeColors.primary.main});
      color: white;
      padding: ${Spacing.sm};
      border-radius: ${BorderRadius.md};
      box-shadow: 0 2px 8px rgba(76, 175, 80, 0.2);
    }
    
    .title {
      font-size: ${Typography.fontSize.xl};
      font-weight: ${Typography.fontWeight.bold};
      color: ${ThemeColors.text.primary};
      margin: 0;
    }
    
    .subtitle {
      font-size: ${Typography.fontSize.sm};
      color: ${ThemeColors.text.secondary};
      margin-top: ${Spacing.xs};
      font-style: italic;
    }
  }
`;

export const TabbedInterface: React.FC<TabbedInterfaceProps> = ({ 
  tabs, 
  defaultActiveTab,
  defaultActiveSubTab,
  activeTab: externalActiveTab,
  variant = 'desktop',
  middleSection,
  showMiddleSectionForTabs = [],
  onTabChange
}) => {
  const { t } = useTranslation();
  const [internalActiveTab, setInternalActiveTab] = useState(defaultActiveTab || tabs[0]?.id);
  const [activeSubTab, setActiveSubTab] = useState<string | null>(defaultActiveSubTab || null);
  const [expandedTabs, setExpandedTabs] = useState<Set<string>>(new Set());

  // Use external activeTab if provided, otherwise use internal state
  const activeTab = externalActiveTab !== undefined ? externalActiveTab : internalActiveTab;
  
  // Update internal state when external activeTab changes
  React.useEffect(() => {
    if (externalActiveTab !== undefined && externalActiveTab !== internalActiveTab) {
      setInternalActiveTab(externalActiveTab);
      // Handle sub-tabs when external tab changes
      const tab = tabs.find(t => t.id === externalActiveTab);
      if (tab?.subTabs && tab.subTabs.length > 0) {
        setExpandedTabs(prev => new Set([...prev, externalActiveTab]));
        if (!activeSubTab || !tab.subTabs.find(st => st.id === activeSubTab)) {
          setActiveSubTab(tab.subTabs[0].id);
        }
      } else {
        setExpandedTabs(new Set());
        setActiveSubTab(null);
      }
    }
  }, [externalActiveTab, internalActiveTab, tabs, activeSubTab]);

  // Scroll to top when tab changes (e.g. Store Visit -> Store Order) so page shows from top
  React.useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTo(0, 0);
    document.body.scrollTo(0, 0);
  }, [activeTab]);

  const activeTabData = tabs.find(tab => tab.id === activeTab);
  const activeSubTabData = activeTabData?.subTabs?.find(subTab => subTab.id === activeSubTab);

  const handleTabClick = (tabId: string) => {
    setInternalActiveTab(tabId);
    onTabChange?.(tabId);
    const tab = tabs.find(t => t.id === tabId);
    
    if (tab?.subTabs && tab.subTabs.length > 0) {
      // If tab has sub-tabs, expand it and select first sub-tab
      setExpandedTabs(prev => new Set([...prev, tabId]));
      if (!activeSubTab || !tab.subTabs.find(st => st.id === activeSubTab)) {
        setActiveSubTab(tab.subTabs[0].id);
      }
    } else {
      // If tab has no sub-tabs, collapse all and clear sub-tab selection
      setExpandedTabs(new Set());
      setActiveSubTab(null);
    }
  };

  const handleSubTabClick = (subTabId: string) => {
    setActiveSubTab(subTabId);
  };

  const toggleExpanded = (tabId: string) => {
    setExpandedTabs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tabId)) {
        newSet.delete(tabId);
        setActiveSubTab(null);
      } else {
        newSet.add(tabId);
        const tab = tabs.find(t => t.id === tabId);
        if (tab?.subTabs && tab.subTabs.length > 0) {
          setActiveSubTab(tab.subTabs[0].id);
        }
      }
      return newSet;
    });
  };

  const getContent = () => {
    if (activeSubTabData) {
      return activeSubTabData.content;
    }
    return activeTabData?.content;
  };

  // Function to get translated tab label
  const getTranslatedTabLabel = (tabId: string, originalLabel: string): string => {
    switch (tabId) {
      case 'store-visit':
        return t('navigation.storeVisit');
      case 'store-order':
        return t('navigation.storeOrder');
      case 'store-history':
        return t('navigation.orderHistory');
      default:
        return originalLabel;
    }
  };

  return (
    <TabContainer variant={variant}>
      <TabHeader variant={variant}>
        {tabs.map((tab) => {
          const IconComponent = tab.icon;
          const isActive = activeTab === tab.id;
          const isExpanded = expandedTabs.has(tab.id);
          const hasSubTabs = tab.subTabs && tab.subTabs.length > 0;
          
          return (
            <React.Fragment key={tab.id}>
              <TabButton
                isActive={isActive}
                variant={variant}
                hasSubTabs={hasSubTabs}
                onClick={() => handleTabClick(tab.id)}
                aria-selected={isActive}
                role="tab"
              >
                {IconComponent && (
                  <TabIcon isActive={isActive}>
                    <IconComponent size={18} />
                  </TabIcon>
                )}
                <TabLabel isCollapsed={false}>{getTranslatedTabLabel(tab.id, tab.label)}</TabLabel>
                {hasSubTabs && (
                  <TabIcon isActive={isActive}>
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </TabIcon>
                )}
              </TabButton>
            </React.Fragment>
          );
        })}
      </TabHeader>
      
      {/* Middle Section - Customer Information and Order Information */}
      {middleSection && showMiddleSectionForTabs.includes(activeTab) && (
        <MiddleSection>
          {middleSection}
        </MiddleSection>
      )}
      
      {/* Sub-tabs container */}
      {expandedTabs.size > 0 && (
        <SubTabContainer isExpanded={true}>
          <SubTabHeader variant={variant}>
            {tabs
              .filter(tab => expandedTabs.has(tab.id))
              .map(tab => 
                tab.subTabs?.map((subTab) => {
                  const SubIconComponent = subTab.icon;
                  const isSubActive = activeSubTab === subTab.id;
                  
                  return (
                    <SubTabButton
                      key={subTab.id}
                      isActive={isSubActive}
                      onClick={() => handleSubTabClick(subTab.id)}
                      aria-selected={isSubActive}
                      role="tab"
                    >
                      {SubIconComponent && (
                        <SubTabIcon isActive={isSubActive}>
                          <SubIconComponent size={14} />
                        </SubTabIcon>
                      )}
                      <SubTabLabel>{subTab.label}</SubTabLabel>
                    </SubTabButton>
                  );
                })
              )
              .flat()}
          </SubTabHeader>
        </SubTabContainer>
      )}
      
      <TabContent variant={variant} role="tabpanel">
        {getContent()}
      </TabContent>
    </TabContainer>
  );
};